import * as mime from 'mime-types';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { Info } from '@mui/icons-material';
import { randomString } from 'utils/string';
import CustomButton from 'components/CustomButton';

export const useStyles = makeStyles((theme) => ({
  input: {
    display: 'none',
  },
  iconButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const Props = {
  label: PropTypes.string,
  max: PropTypes.number,
  sizeMb: PropTypes.number,
  extensions: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

const DefaultProps = {
  label: 'Upload file',
  max: null,
  sizeMb: 5,
  extensions: ['jpg', 'png'],
};

export default function FileButton({
  label,
  max,
  sizeMb,
  extensions,
  onChange,
  disabled,
  onClick,
  ...rest
}) {
  const classes = useStyles();
  const inputRef = useRef();
  const [helpElement, setHelpElement] = useState();
  const elementId = randomString();
  const mimeTypes = extensions.map((extension) => mime.lookup(extension));

  function handleClick(event) {
    onChange();

    if (onClick) {
      onClick(event);
    }
  }

  async function handleChange({
    target: {
      files: [file],
    },
  }) {
    if (file) {
      const isValidSize = file.size / 1000000 <= sizeMb;
      const isValidType = mimeTypes.includes(file.type);

      if (!isValidSize) {
        onChange(undefined, 'Invalid file size.');
      } else if (!isValidType) {
        onChange(undefined, 'Invalid file type.');
      } else {
        onChange(file);
      }
    }

    inputRef.current.value = '';
  }

  return (
    <Box display="inline-flex" alignItems="center">
      <input
        id={elementId}
        className={classes.input}
        accept={mimeTypes.join(',')}
        type="file"
        ref={inputRef}
        onChange={handleChange}
      />

      <label htmlFor={!disabled ? elementId : null}>
        <CustomButton component="span" disabled={disabled} onClick={handleClick} {...rest}>
          {label}
        </CustomButton>
      </label>

      <IconButton
        className={classes.iconButton}
        size="small"
        onClick={(event) => setHelpElement(event.currentTarget)}
        aria-label="More Information"
      >
        <Info />
      </IconButton>

      <Popover
        open={!!helpElement}
        anchorEl={helpElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => setHelpElement()}
      >
        <Box p={2}>
          {max && (
            <Typography>
              <b>Max files</b>: {max}
            </Typography>
          )}
          <Typography>
            <b>Max size per file</b>: {sizeMb}MB
          </Typography>
          <Typography>
            <b>File types</b>: {extensions.join(', ')}
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
}

FileButton.propTypes = Props;
FileButton.defaultProps = DefaultProps;
